import { t } from "@lingui/macro";
import { type CreditExpiry, creditExpirySchema } from "@reactive-resume/dto";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { axios } from "@/client/libs/axios";
import { useUser } from "@/client/services/user";

/**
 * Structure réellement renvoyée par
 * GET /engagement/account-overview.
 *
 * Ce schéma valide la réponse du backend avant de
 * la transformer pour les composants du dashboard.
 *
 * NB : `currentPlan.code` est volontairement une simple chaîne — le
 * backend est la source de vérité et peut servir tout nouveau plan.
 * Un enum strict côté client ferait échouer le parse ENTIER de
 * l'overview (topbar et compteurs retombant silencieusement à 0).
 * La coercition vers le type affiché se fait dans `resolvePlanCode`.
 */
const accountOverviewResponseSchema = z.object({
  currentPlan: z
    .object({
      code: z.string().nullable(),
      name: z.string(),
      validFrom: z.coerce.date(),
      validTo: z.coerce.date(),
    })
    .nullable(),

  downloadCredits: z.object({
    available: z.number().int().nonnegative(),
    expiring: z.array(creditExpirySchema).default([]),
  }),

  aiCredits: z.object({
    available: z.number().int().nonnegative(),
  }),

  wallet: z.object({
    balanceFcfa: z.number().int(),
  }),

  loyalty: z.object({
    balance: z.number().int().nonnegative(),
  }),

  rewards: z.object({
    referralFcfa: z.number().int().nonnegative(),

    ambassadorFcfa: z.number().int().nonnegative(),
  }),
});

export type AccountOverview = {
  currentPlan: {
    code: "FREE" | "SMART_START" | "SMART_JOB" | "SMART_PRO";

    name: string;
    validFrom: Date | null;
    validTo: Date | null;
  };

  downloadCredits: number;
  downloadCreditsExpiring: CreditExpiry[];
  aiCredits: number;
  walletFcfa: number;
  loyaltyPoints: number;
  referralRewardsFcfa: number;
  ambassadorRewardsFcfa: number;
  totalRewardsFcfa: number;
};

export const ACCOUNT_OVERVIEW_QUERY_KEY = ["user-space", "account-overview"] as const;

const emptyOverview: AccountOverview = {
  currentPlan: {
    code: "FREE",
    get name() {
      return t`Gratuit`;
    },
    validFrom: null,
    validTo: null,
  },

  downloadCredits: 0,
  downloadCreditsExpiring: [],
  aiCredits: 0,
  walletFcfa: 0,
  loyaltyPoints: 0,
  referralRewardsFcfa: 0,
  ambassadorRewardsFcfa: 0,
  totalRewardsFcfa: 0,
};

/**
 * Coercition sûre du code plan servi par le backend vers le type affiché.
 * Une valeur inconnue retombe sur « FREE » au lieu de casser l'overview.
 */
const PLAN_CODES = ["FREE", "SMART_START", "SMART_JOB", "SMART_PRO"] as const;

const resolvePlanCode = (code: string | null): AccountOverview["currentPlan"]["code"] =>
  (PLAN_CODES as readonly string[]).includes(code ?? "")
    ? (code as AccountOverview["currentPlan"]["code"])
    : "FREE";

/**
 * Convertit la structure imbriquée du backend
 * vers la structure simple déjà utilisée par
 * la topbar, la sidebar et le layout.
 */
const normalizeAccountOverview = (
  response: z.infer<typeof accountOverviewResponseSchema>,
): AccountOverview => {
  const referralRewardsFcfa = response.rewards.referralFcfa;

  /*
   * Commissions ambassadeur créditées — servies par
   * GET /engagement/account-overview depuis Phase B.
   */
  const ambassadorRewardsFcfa = response.rewards.ambassadorFcfa;

  return {
    currentPlan: response.currentPlan
      ? {
          code: resolvePlanCode(response.currentPlan.code),

          name: response.currentPlan.name,

          validFrom: response.currentPlan.validFrom,

          validTo: response.currentPlan.validTo,
        }
      : {
          ...emptyOverview.currentPlan,
        },

    downloadCredits: response.downloadCredits.available,

    downloadCreditsExpiring: response.downloadCredits.expiring,

    aiCredits: response.aiCredits.available,

    walletFcfa: response.wallet.balanceFcfa,

    loyaltyPoints: response.loyalty.balance,

    referralRewardsFcfa,

    ambassadorRewardsFcfa,

    totalRewardsFcfa: referralRewardsFcfa + ambassadorRewardsFcfa,
  };
};

export const useAccountOverview = () => {
  const { user } = useUser();

  const query = useQuery({
    queryKey: [...ACCOUNT_OVERVIEW_QUERY_KEY, user?.id],

    enabled: Boolean(user?.id),

    queryFn: async ({ signal }) => {
      const response = await axios.get<unknown>("/engagement/account-overview", {
        signal,
      });

      /**
       * safeParse — jamais un parse qui throw : une dérive de contrat
       * backend → client (nouveau plan, champ manquant…) ne doit JAMAIS
       * retomber en soldes à 0 silencieux. L'erreur est loggée avec ses
       * issues et remontée au composant via `error`.
       */
      const parsed = accountOverviewResponseSchema.safeParse(response.data);

      if (!parsed.success) {
        console.error(
          "[account-overview] Réponse inattendue du serveur — vérifier GET /engagement/account-overview :",
          parsed.error.issues,
        );

        throw parsed.error;
      }

      return normalizeAccountOverview(parsed.data);
    },

    /*
     * Les valeurs changent après un paiement,
     * un téléchargement ou une récompense.
     */
    staleTime: 5_000,

    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: 1,
  });

  return {
    overview: query.data ?? emptyOverview,

    loading: query.isPending,

    refreshing: query.isFetching,

    error: query.error,

    refetch: query.refetch,
  };
};
